// pages/WebsiteLinks/ListArchivedData.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from 'react-data-table-component';

import {
    Box, Typography, Alert, TextField, IconButton, CircularProgress, Button, Tooltip, useTheme, Card, CardContent, Grid
} from "@mui/material";
import { Delete as DeleteIcon, History as RetrainIcon } from "@mui/icons-material";

export default function ListArchivedData({ refresh, setRefresh, chatbotId, permission }) {
    const theme = useTheme();
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isRetraining, setIsRetraining] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            const response = await axiosInstance.get(`/get-archived-website-links`, { params });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data || [];
            setDocuments(responseData);
            setFilteredDocuments(responseData);
        } catch (err) {
            setError(`Error fetching archived data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        fetchDocuments();
    }, [refresh, fetchDocuments]);

    useEffect(() => {
        const lowerCaseSearch = searchText.toLowerCase();
        setFilteredDocuments(documents.filter(doc =>
            doc.website_link?.toLowerCase().includes(lowerCaseSearch) ||
            doc.title_tag?.toLowerCase().includes(lowerCaseSearch)
        ));
    }, [searchText, documents]);

    const handleRetrain = async (ids) => {
        setIsRetraining(true);
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.post(`/retrain-archived-data`, { link_ids: ids }, { params });
            setError(`${ids.length} item(s) sent for retraining.`);
            setSelectedRows([]);
            fetchDocuments();
            if(setRefresh) setRefresh(prev => !prev);
        } catch (err) {
            setError(`Retraining failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsRetraining(false);
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm(`PERMANENTLY DELETE this item? This action cannot be undone.`)) return;
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.delete(`/delete-website-link/${docId}`, { params });
            setError("Item permanently deleted.");
            fetchDocuments();
        } catch (err) {
            setError(`Delete failed: ${err.response?.data?.message || err.message}`);
        }
    };
    
  
    const customDataTableStyles = {
        table: { style: { backgroundColor: theme.palette.background.paper, } },
        headRow: { style: { backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9", borderBottom: `1px solid ${theme.palette.divider}`, }, },
        headCells: { style: { fontWeight: 600, color: theme.palette.text.primary, } },
        rows: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, '&:hover': { backgroundColor: `${theme.palette.action.hover} !important` } } },
        pagination: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, borderTop: `1px solid ${theme.palette.divider}` } },
        expanderRow: { style: { backgroundColor: theme.palette.action.hover } },
    };

    const columns = [
        { name: 'URL', selector: row => row.website_link, sortable: true, grow: 2, cell: row => <Tooltip title={row.website_link}><Typography variant="body2" noWrap sx={{ maxWidth: { xs: '150px', sm: '300px' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{row.website_link}</Typography></Tooltip> },
        { name: 'Page Name', selector: row => row.title_tag, sortable: true, hide: 'sm', cell: row => <Tooltip title={row.title_tag}><Typography variant="body2" noWrap>{row.title_tag || '(No Title)'}</Typography></Tooltip> },
        { name: 'Date Archived', selector: row => row.updated_date, sortable: true, hide: 'md', format: row => new Date(row.updated_date).toLocaleString() },
        { name: 'Actions', width: '100px', cell: row => (
            <Box>
                {permission.canModify && <Tooltip title="Retrain & Unarchive"><IconButton size="small" color="primary" onClick={() => handleRetrain([row.id])} disabled={isRetraining}><RetrainIcon fontSize="small" /></IconButton></Tooltip>}
                {permission.canDelete && <Tooltip title="Delete Permanently"><IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
            </Box>
        )},
    ];

    return (
        <Box>
            <Card>
                <CardContent>
                    <Grid container spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Grid item xs={12} md={6}>
                            {permission.canModify && <Button variant="contained" onClick={() => handleRetrain(selectedRows.map(r => r.id))} disabled={selectedRows.length === 0 || isRetraining} startIcon={isRetraining ? <CircularProgress size={16} /> : <RetrainIcon />} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Retrain ({selectedRows.length})</Button>}
                        </Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" size="small" placeholder="Search Archived Data..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></Grid>
                    </Grid>

                    {error && <Alert severity={error.includes("success") || error.includes("sent for retraining") ? "success" : "error"} sx={{ mb: 2 }}>{error}</Alert>}

                    {loading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" py={4}><CircularProgress /><Typography mt={2}>Loading Archived Data...</Typography></Box>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredDocuments}
                            pagination
                            selectableRows
                            onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                            customStyles={customDataTableStyles}
                            theme={theme.palette.mode === 'dark' ? 'dark' : 'default'}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}